import React from "react";
import { cn } from "@/lib/utils";
import { Loader2, Dumbbell } from "lucide-react";

export function Logo({ className, showText = true }) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-primary-foreground shadow-sm">
        <Dumbbell className="h-5 w-5" strokeWidth={2.4} />
      </div>
      {showText && <span className="font-display text-xl font-bold tracking-tight">FitBersama</span>}
    </div>
  );
}

export function PageLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Loader2 className="h-7 w-7 animate-spin text-primary" />
    </div>
  );
}

export function SectionHeader({ title, subtitle, action }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h2 className="font-display text-2xl font-semibold tracking-tight md:text-3xl">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function StatCard({ icon: Icon, label, value, unit, sub, accent, className, testid, children }) {
  return (
    <div
      data-testid={testid}
      className={cn(
        "group rounded-2xl border bg-card p-5 transition-transform duration-200 ease-out hover:-translate-y-1 hover:border-primary/40",
        className
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
        {Icon && (
          <span className={cn("grid h-8 w-8 place-items-center rounded-lg", accent || "bg-accent text-accent-foreground")}>
            <Icon className="h-4 w-4" />
          </span>
        )}
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="font-display text-3xl font-semibold tabular-nums tracking-tight">{value ?? "—"}</span>
        {unit && <span className="text-sm text-muted-foreground">{unit}</span>}
      </div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
      {children}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action, className }) {
  return (
    <div className={cn("flex flex-col items-center justify-center rounded-2xl border border-dashed py-16 text-center", className)}>
      {Icon && (
        <div className="mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-accent text-accent-foreground">
          <Icon className="h-7 w-7" />
        </div>
      )}
      <h3 className="font-display text-lg font-semibold">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

const BMI_COLORS = {
  underweight: "bg-sky-500/15 text-sky-500",
  normal: "bg-primary/15 text-primary",
  overweight: "bg-warning/15 text-warning",
  obese: "bg-destructive/15 text-destructive",
};

export function BmiBadge({ category, t }) {
  if (!category) return null;
  return (
    <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize", BMI_COLORS[category])}>
      {t ? t(category) : category}
    </span>
  );
}
